import { createSlice, createAsyncThunk, SerializedError, PayloadAction } from '@reduxjs/toolkit'

import { createActionType } from './utils'
import { initialCompanyState } from './initialStates'
import { CompanyState } from './types'
import { Company, CompanyMember } from '../models/client/company'
import { companyClient } from '../clients/CompanyClient'

const REDUCER_NAME = 'company'

const createCompanyActionType = (name: string): string => createActionType(name, REDUCER_NAME)

export const getCompanyAction = createAsyncThunk(
    createCompanyActionType('init'),
    ({ token, companyId }: { token: string; companyId: string }) =>
        companyClient.getCompany({ token, companyId })
)

const slice = createSlice({
    name: REDUCER_NAME,
    initialState: initialCompanyState,
    reducers: {
        clearCompanyAction(draft) {
            draft.value = null
            draft.loading = false
            draft.error = null
        },
        updateMembership(draft, { payload }: PayloadAction<CompanyMember>) {
            if (!draft.value?.members) {
                draft.error = {
                    message: 'Cannot add member, company not initiated',
                }
                return
            }
            draft.value.members[payload.id] = payload
        },
        deleteMember(draft, { payload }: PayloadAction<string>) {
            delete draft.value?.members[payload]
        },
    },
    extraReducers(builder) {
        builder.addCase(getCompanyAction.pending, (draft) => {
            draft.value = null
            draft.loading = true
            draft.error = null
        })
        builder.addCase(getCompanyAction.rejected, (draft, { error }) => {
            draft.error = error
            draft.loading = false
            draft.value = null
        })
        builder.addCase(getCompanyAction.fulfilled, (draft, { payload }) => {
            draft.value = payload
            draft.loading = false
            draft.error = null
        })
    },
})

export const { reducer } = slice

export const { clearCompanyAction, updateMembership, deleteMember } = slice.actions

type State = {
    company: CompanyState
}

export const selectCompany = (state: State): Company | null => state.company.value

export const selectCompanyId = (state: State): string | null => state.company.value?.id || null

export const selectCompanyLoading = (state: State): boolean => state.company.loading

export const selectCompanyError = (state: State): SerializedError | null => state.company.error

export const selectCompanyMemberList = (state: State): CompanyMember[] =>
    Object.values(state.company.value?.members || {})

export const selectCompanyMemberDirectory = (state: State): Record<string, CompanyMember> | null =>
    state.company.value?.members || null
